import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { throttle } from 'lodash';
// components
import { Row } from '../../foundations/Grid';
import AdvisorConnect from '../AdvisorConnect';
import AgentPhoto from '../AdvisorConnect/components/agentPhoto';
import Modal from '../../components/Modal';
import SectionContainer from '../../components/SectionContainer';
import SquareWithTwoOppositeDiagonalArrows from '../../foundations/Icon/icons/utility/SquareWithTwoOppositeDiagonalArrows';
import theme from '../../foundations/theme';
import Typography from '../../foundations/Typography';
// styles
import {
  ColStyledHeading,
  ColStyledTeamMember,
  SpanStyledAgent,
  SpanStyledIcon,
} from './styles';

export const useModalResize = (modalHeight, setModalHeight) => {
  const LISTENER_THROTTLE = 100;
  const handleResize = throttle(() => {
    setModalHeight(window.innerHeight <= 730 ? 'auto' : 'fit-content');
  }, LISTENER_THROTTLE);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  return [modalHeight];
};

const TeamBiosModule = ({
  backgroundVariant,
  className,
  config,
  detailsModalAriaLabel,
  detailsModalId = 'team-bios-details',
  envConfig,
  sectionId,
  themeType = 'lightTheme',
  ...rest
}) => {
  const [modalHeight, setModalHeight] = useState('auto');

  useModalResize(modalHeight, setModalHeight);

  const afterOpenModalFunction = (modalContentSectionId) => {
    const modalContentRow = document.querySelector('.nmx-row-modal-content');
    if (modalContentRow) {
      modalContentRow.style.height = 'fit-content';
    }
    const modalContentSection = document.querySelector(modalContentSectionId);
    if (modalContentSection) {
      modalContentSection.style.paddingBottom = 0;
      modalContentSection.style.paddingTop = 0;
    }
  };

  return (
    <SectionContainer
      id={sectionId}
      data-testid='team-bios-container'
      className={className}
      backgroundVariant={backgroundVariant}
      moduleName={`${sectionId}-module`}
      moduleVariation='A'
      themeType={themeType}
      {...rest}>
      <div className={`${sectionId}-module__team-member-cards`}>
        {config.teamGroups && config.teamGroups.length > 0 && config.teamGroups.map((teamGroup, index) => (
          <Fragment key={teamGroup.groupId}>
            <Row>
              <ColStyledHeading
                className={`${sectionId}-module__team-group`}
                index={index}>
                <Typography
                  className={`${sectionId}-module__team-group-name`}
                  component='h2'
                  styles={{ color: theme.colors.blue.blueLight20 }}
                  variant='h4'>
                  {teamGroup.groupName}
                </Typography>
              </ColStyledHeading>
            </Row>
            <Row>
              {teamGroup.teamMembers && teamGroup.teamMembers.length > 0 && teamGroup.teamMembers.map((teamMember) => (
                <ColStyledTeamMember
                  className={`${sectionId}-module__team-member-card`}
                  detailsModalId={detailsModalId}
                  key={teamMember.id}
                  xsmall={12}
                  small={6}
                  medium={3}
                  large={2}>
                  <Modal
                    backgroundVariant='lightC'
                    buttonCloseAriaLabel={`Dismiss the ${detailsModalAriaLabel}`}
                    buttonCloseClassName={`${detailsModalId}-close-modal-button`}
                    buttonCloseId={`${detailsModalId}-${teamMember.id}-close-modal-button`}
                    buttonOpenAriaLabel={`Open ${detailsModalAriaLabel} for ${teamMember.fullName}`}
                    buttonOpenClassName={`${detailsModalId}-open-modal-button`}
                    buttonOpenId={`${detailsModalId}-${teamGroup.groupId}-${teamMember.id}-open-modal-button`}
                    buttonOpenVariant='tertiary'
                    buttonText={
                      <SpanStyledAgent>
                        <AgentPhoto
                          agentDisplayVariant='teamMemberBio'
                          agentImageUrl={teamMember.image}
                          agentName={{
                            firstName: teamMember.firstName,
                            fullName: teamMember.fullName,
                            lastName: teamMember.lastName,
                          }}
                          photoProportion={teamGroup.photoProportion}
                          prependPictureChildren={
                            <SpanStyledIcon>
                              <SquareWithTwoOppositeDiagonalArrows />
                            </SpanStyledIcon>
                          }
                        />
                        <Typography
                          disableBottomPadding
                          weight='500' // matches heading fontWeight
                        >
                          {teamMember.fullName}
                        </Typography>
                        <Typography>
                          {teamMember.role}
                        </Typography>
                      </SpanStyledAgent>
                    }
                    contentLabel={detailsModalAriaLabel}
                    dataTestIdButtonModalOpen={`team-bios-button-${teamGroup.groupId}-${teamMember.id}`}
                    modalClassName={`${detailsModalId}-modal`}
                    modalId={detailsModalId}
                    modalAriaLabel={detailsModalAriaLabel}
                    modalName='teamBios'
                    modalParentSelector={`#${sectionId}-section-container`}
                    onModalAfterOpen={() => {
                      afterOpenModalFunction(`#${detailsModalId}-content-card-section-container`);
                    }}
                    variant='large'>
                    <AdvisorConnect
                      agentDisplayVariant='teamMemberBio'
                      agentObject={{
                        briefBio: teamMember.bio,
                        firstName: teamMember.firstName,
                        fullName: teamMember.fullName,
                        lastName: teamMember.lastName,
                        photo: teamMember.image,
                        title: teamMember.role,
                      }}
                      agentPhotoProportion={teamGroup.photoProportion}
                      backgroundVariant='lightC'
                      sectionId={`${detailsModalId}-content-card`}
                    />
                  </Modal>
                </ColStyledTeamMember>
              ))}
            </Row>
          </Fragment>
        ))}
      </div>
    </SectionContainer>
  );
};

TeamBiosModule.propTypes = {
  /** backgroundVariant allows section to be different background color based on themeType */
  backgroundVariant: PropTypes.oneOf(['darkDefault', 'lightA', 'lightB', 'lightC']),
  /** optional custom class name */
  className: PropTypes.string,
  /** team bios config */
  config: {
    /** team list of team groups and team members */
    teamGroups: PropTypes.arrayOf(
      PropTypes.shape({
        /** name of team group */
        groupName: PropTypes.string.isRequired,
        /** id of team group (no spaces) */
        groupId: PropTypes.string.isRequired,
        /** description of team group */
        groupDescription: PropTypes.string,
        /** team member photo proportion */
        photoProportion: PropTypes.oneOf(['portrait', 'square']),
        /** list of team members */
        teamMembers: PropTypes.arrayOf(
          PropTypes.shape({
            /** first name of team member */
            firstName: PropTypes.string.isRequired,
            /** last name of team member */
            lastName: PropTypes.string.isRequired,
            /** full name of team member */
            fullName: PropTypes.string.isRequired,
            /** role of team member */
            role: PropTypes.string.isRequired,
            /** image of team member */
            image: PropTypes.string,
            /** bio of team member */
            bio: PropTypes.string,
            /** id of team member (no spaces) */
            id: PropTypes.string.isRequired,
          }),
        ).isRequired,
      }),
    ).isRequired,
  }.isRequired,
  /** accessible name of team bios details modal */
  detailsModalAriaLabel: PropTypes.string,
  /** id of team bios details modal (ensure no spaces) */
  detailsModalId: PropTypes.string,
  /** env config */
  envConfig: PropTypes.shape({
    basePath: PropTypes.string,
    nmxTemplateVersion: PropTypes.string,
  }),
  /** id for the section */
  sectionId: PropTypes.string.isRequired,
  /** optional themeType */
  themeType: PropTypes.oneOf(['lightTheme', 'darkTheme']),
};

export default TeamBiosModule;
